import React, { useCallback, useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';
import { useHistory, useParams } from 'react-router-dom';
import { Card, CardBody, CardHeader, CardSubtitle, Col, Row } from 'reactstrap';
import * as Yup from 'yup';
import lodash from 'lodash';
import { shallowEqual, useSelector } from 'react-redux';
import { access } from 'constants/index';
import authUtil from 'util/auth';
import formBuilder from 'ui-blocks/form-builder';
import {
   ChannelSelectSSA,
   ColorPickerInput,
   PixelInput,
   PublisherSelectSSA,
   SelectInput,
   SiteSelectSSA,
   TextInput,
} from 'ui-blocks/form-elements';
import { ButtonsSubmitExport } from 'ui';
import AccessControl from 'components/AccessControl';
import useModalState from 'util/hook/useModalState';
import { ActionLink } from 'ui-blocks/data-display';
import AdUnitVisualizer from './AdUnitVisualizer';
import ColorFieldItem from './ColorFieldItem';
import CreateChannelModal from './component/CreateChannelModal';
import FieldItem from './FieldItem';
import config from './formConfig';
import Section from './Section';

const formBuilderBag = formBuilder.build(config);
const { withComponentWrapper } = formBuilderBag;

const numAdsSelectOptions = Array.from({ length: 5 }, (v, i) => ({
   label: `${ i + 1 }`,
   value: i + 1,
}));

const fieldAttr = {
   borderWidth: {
      min: 0,
      max: 10,
   },
};

const appAdminAccess = access.getAdminAccessByApp.syndicatedSponsoredAds;

const AdUnitForm = (props) => {
   const { mode } = useParams();
   const [modeLabel, setModeLabel] = useState();
   const [numberChangeListenerForChannelSelect, setNumberChangeListenerForChannelSelect] = useState();
   const history = useHistory();

   const userPartnerRoleList = useSelector(
      (state) => (state?.authReducer?.loggedInUserPartner?.roles || []),
      shallowEqual,
   );

   const {
      isOpen: isOpenChannel,
      toggleModal: toggleChannel,
      openModal: openChannel,
      closeModal: closeChannel,
   } = useModalState();

   const [validationSchema, setValidationSchema] = useState(undefined);

   useEffect(() => {
      const innerValidationSchema = {
         site: Yup.object().required('Required!'),
         channel: Yup.object().required('Required!'),
         adUnitName: Yup.string().required(),
         borderWidth: Yup.number().min(fieldAttr.borderWidth.min).max(fieldAttr.borderWidth.max).required(),
         fontFamily: Yup.object().required('Required!'),
         fontSize: Yup.object().required('Required!'),
         numAds: Yup.object().required('Required!'),
      };

      if (authUtil.hasAuthAccess(appAdminAccess)) {
         innerValidationSchema.publisher = Yup.object().required('Required!');
      }
      setValidationSchema(Yup.object().shape(innerValidationSchema));
   }, [userPartnerRoleList]);

   useEffect(() => {
      let newModeLabel;
      switch (mode) {
         case 'create':
            newModeLabel = 'Create';
            break;
         case 'edit':
            newModeLabel = 'Edit';
            break;
         default:
            newModeLabel = '??';
      }
      setModeLabel(newModeLabel);
   }, [mode]);

   const handleCloseChannel = useCallback((statusCode) => {
      if (+statusCode === 200) {
         setNumberChangeListenerForChannelSelect(Date.now());
      }
      closeChannel();
   }, []);

   return (
      <>
         {
            isOpenChannel
            && (
               <CreateChannelModal
                  toggle={toggleChannel}
                  isOpen={isOpenChannel}
                  close={handleCloseChannel}
               />
            )
         }
         <Formik
            initialValues={props.initialValues}
            validationSchema={validationSchema}
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ (values, { setSubmitting, setErrors }) => {
               const {
                  borderWith,
                  channel,
                  fontFamily,
                  fontSize,
                  includeLogo,
                  logoSize,
                  numAds,
                  publisher,
                  site,
                  ...restValues
               } = values;

               const payload = {
                  ...restValues,
                  borderWith: `${ borderWith }`,
                  channelId: channel?.id,
                  fontFamily: fontFamily?.value,
                  fontSize: fontSize?.value,
                  includeLogo: includeLogo?.value,
                  logoSize: logoSize?.value,
                  numAds: numAds?.value,
                  publisherId: publisher?.id,
                  siteId: site?.id,
               };

               const { create, edit } = config.settings.external.api.save;
               let apiCall = null;

               const isModeCreate = mode === 'create';
               if (isModeCreate) {
                  apiCall = create;
               } else if (mode === 'edit') {
                  apiCall = edit;
               }

               if (apiCall !== null && lodash.isFunction(apiCall)) {
                  setSubmitting(true);
                  apiCall(payload).then((res) => {
                     const data = res?.data?.data;
                     if (!Number.isNaN(data?.id)) {
                        if (isModeCreate) {
                           toastr.success('Ad Unit Created Successfully!');
                           history.replace(`${ config.settings.internal.routing.basePath }/edit/${ data?.id }`);
                        } else {
                           toastr.success('Update Success!');
                           props.reloadInitialValues();
                        }
                     } else if (lodash.isPlainObject(data?.error)) {
                        setErrors(data.error);
                     } else {
                        toastr.error('Something went wrong. Please try again later.');
                     }

                     setSubmitting(false);
                  });
               } else {
                  console.error('No mode matching.');
               }
            } }
         >
            {
               (formik) => {
                  const clearFieldValue = (fieldName) => {
                     formik.setFieldValue(fieldName, undefined, false);
                     formik.setFieldTouched(fieldName, false, false);
                  };

                  return (
                     <Form className='container'>
                        <Row>
                           <Col xs={12}>
                              <h4>
                                 {modeLabel}
                                 &nbsp;Ad Unit
                              </h4>
                           </Col>
                        </Row>
                        <Row>
                           <Col
                              xs={12}
                           >
                              <Card>
                                 <CardBody>
                                    <Row>
                                       <Col
                                          xs={12}
                                          xl={6}
                                       >
                                          <FieldItem
                                             label='Ad Unit Name'
                                             name='adUnitName'
                                             component={TextInput}
                                          />
                                          <FieldItem
                                             label='# Ads/Unit'
                                             name='numAds'
                                             component={SelectInput}
                                             options={numAdsSelectOptions}
                                          />
                                       </Col>
                                       <Col
                                          xs={12}
                                          xl={6}
                                       >
                                          <AccessControl
                                             access={appAdminAccess}
                                          >
                                             <FieldItem
                                                label='Publisher'
                                                name='publisher'
                                                component={PublisherSelectSSA}
                                                isSearchable
                                                onChange={() => {
                                                   clearFieldValue('site');
                                                   clearFieldValue('channel');
                                                }}
                                             />
                                          </AccessControl>
                                          <FieldItem
                                             label='Site'
                                             name='site'
                                             component={SiteSelectSSA}
                                             isSearchable
                                             onChange={() => {
                                                clearFieldValue('channel');
                                             }}
                                             suggestionDependencies={{
                                                publisher: formik?.values?.publisher,
                                             }}
                                          />
                                          <FieldItem
                                             label='Channel'
                                             name='channel'
                                             component={ChannelSelectSSA}
                                             isSearchable
                                             suggestionDependencies={{
                                                publisher: formik?.values?.publisher,
                                                site: formik?.values?.site,
                                             }}
                                             numberChangeToRefresh={numberChangeListenerForChannelSelect}
                                          >
                                             <ActionLink
                                                label='Create Channel'
                                                onClick={openChannel}
                                             />
                                          </FieldItem>
                                       </Col>
                                    </Row>
                                    <Row>
                                       <Col xs={12}>
                                          <h5>
                                             Customize Ad Unit
                                          </h5>
                                       </Col>
                                       <Col xs={12} md={6}>
                                          <Section
                                             label='Color'
                                          >
                                             <div className='d-flex flex-wrap justify-content-between'>
                                                <ColorFieldItem
                                                   label='Title'
                                                   name='titleColor'
                                                   component={ColorPickerInput}
                                                />
                                                <ColorFieldItem
                                                   label='URL'
                                                   name='urlColor'
                                                   component={ColorPickerInput}
                                                />
                                                <ColorFieldItem
                                                   label='Ad Copy'
                                                   name='copyColor'
                                                   component={ColorPickerInput}
                                                />
                                                <ColorFieldItem
                                                   label='Background'
                                                   name='backgroundColor'
                                                   component={ColorPickerInput}
                                                />
                                                <ColorFieldItem
                                                   label='Border'
                                                   name='borderColor'
                                                   component={ColorPickerInput}
                                                />
                                             </div>
                                          </Section>
                                       </Col>
                                       <Col
                                          xs={12}
                                          sm={6}
                                       >
                                          <Section label='Border'>
                                             <FieldItem
                                                label='Border Width'
                                                name='borderWidth'
                                                component={PixelInput}
                                                step='1'
                                                min={fieldAttr.borderWidth.min}
                                                max={fieldAttr.borderWidth.max}
                                             />
                                          </Section>
                                       </Col>
                                       <Col
                                          xs={12}
                                          sm={6}
                                       >
                                          <Section label='Font'>
                                             <FieldItem
                                                label='Font Family'
                                                name='fontFamily'
                                                component={SelectInput}
                                                options={
                                                   [
                                                      {
                                                         label: 'Arial',
                                                         value: 'arial',
                                                      },
                                                      {
                                                         label: 'Courier',
                                                         value: 'courier',
                                                      },
                                                      {
                                                         label: 'Georgia',
                                                         value: 'georgia',
                                                      },
                                                      {
                                                         label: 'Helvetica',
                                                         value: 'helvetica',
                                                      },
                                                      {
                                                         label: 'Times',
                                                         value: 'times',
                                                      },
                                                      {
                                                         label: 'Verdana',
                                                         value: 'verdana',
                                                      },
                                                   ]
                                                }
                                             />
                                             <FieldItem
                                                label='Font Size'
                                                name='fontSize'
                                                component={SelectInput}
                                                options={
                                                   [
                                                      {
                                                         label: 'Small',
                                                         value: 'small',
                                                      },
                                                      {
                                                         label: 'Medium',
                                                         value: 'medium',
                                                      },
                                                      {
                                                         label: 'Large',
                                                         value: 'large',
                                                      },
                                                   ]
                                                }
                                             />
                                          </Section>
                                       </Col>
                                       <Col
                                          xs={12}
                                          sm={6}
                                       >
                                          <Section label='Logo'>
                                             <FieldItem
                                                label='Include Logo'
                                                name='includeLogo'
                                                component={SelectInput}
                                                options={
                                                   [
                                                      {
                                                         label: 'Yes',
                                                         value: 'y',
                                                      },
                                                      {
                                                         label: 'No',
                                                         value: 'n',
                                                      },
                                                   ]
                                                }
                                             />
                                             <FieldItem
                                                label='Logo Size'
                                                name='logoSize'
                                                component={SelectInput}
                                                options={
                                                   [
                                                      {
                                                         label: 'Small',
                                                         value: 32,
                                                      },
                                                      {
                                                         label: 'Medium',
                                                         value: 48,
                                                      },
                                                      {
                                                         label: 'Large',
                                                         value: 64,
                                                      },
                                                   ]
                                                }
                                             />
                                          </Section>
                                       </Col>
                                    </Row>
                                    <div className='text-center mt-4'>
                                       <ButtonsSubmitExport/>
                                    </div>
                                 </CardBody>
                              </Card>
                           </Col>
                           <Col
                              xs={12}
                              xl={6}
                           >
                              <Card>
                                 <CardHeader>
                                    <h5>
                                       Example
                                    </h5>
                                    <CardSubtitle
                                       tag='h6'
                                       className='mb-2 text-muted'
                                    >
                                       This is for preview purposes only. It is not the actual size of the unit. You can control the size of the unit on your page.
                                    </CardSubtitle>
                                 </CardHeader>
                                 <CardBody>
                                    <AdUnitVisualizer
                                       backgroundColor={formik.values.backgroundColor}
                                       titleColor={formik.values.titleColor}
                                       urlColor={formik.values.urlColor}
                                       copyColor={formik.values.copyColor}
                                       borderColor={formik.values.borderColor}
                                       fontFamily={lodash.isString(formik.values?.fontFamily) ? formik.values.fontFamily : formik.values?.fontFamily?.value}
                                       fontSize={lodash.isString(formik.values?.fontSize) ? formik.values.fontSize : formik.values?.fontSize?.value}
                                       borderWidth={+formik.values.borderWidth}
                                       numAds={lodash.isNumber(formik.values?.numAds) || lodash.isString(formik.values?.numAds) ? formik.values.numAds : formik.values?.numAds?.value}
                                       includeLogo={formik.values.includeLogo?.value === 'y'}
                                       logoSize={ +formik.values.logoSize?.value }
                                    />
                                 </CardBody>
                              </Card>
                           </Col>
                        </Row>
                        {/*<div className='text-center mt-4'>*/}
                        {/*   <ButtonsSubmitExport/>*/}
                        {/*</div>*/}
                     </Form>
                  );
               }
            }
         </Formik>
      </>
   );
};

AdUnitForm.propTypes = {
   initialValues: PropTypes.objectOf(PropTypes.any),
   reloadInitialValues: PropTypes.func.isRequired,
};

AdUnitForm.defaultProps = {
   initialValues: undefined,
};

export const { path } = formBuilderBag;
export default withComponentWrapper(AdUnitForm);
